/**
 * Models for the round-3 mobs (entities/mobs3): rabbit, goat, turtle, mooshroom, polar bear,
 * ocelot, snow golem, axolotl, dolphin, guardian (+ elder, beam), shulker (+ bullet), evoker
 * (+ fangs), vex, ravager. Vanilla part layouts (ModelPart boxes, pivots, UV offsets) over the
 * real entity sheets in public/mc/entity (model px space, y down, front -z, feet at y = 24).
 * Babies of these types are the adult model at half scale.
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import { entityMaterial, sheetTexture } from './core';
import { applyHandheld, itemSpriteMesh } from './extras';
import { CowModel } from './mobs';
import type { OwAnim } from './overworldModels';
import { Rig } from './overworldModels2';

const PI = Math.PI;

/** Half-scale the whole model for a baby (feet stay on the ground). */
function babyScale(parts: THREE.Object3D, baby: boolean | undefined, adult = 1): void {
  const s = baby ? adult * 0.5 : adult;
  parts.scale.setScalar(s);
  parts.position.set(0, 24 * (1 - s), 0);
}

/** A flat quad in the XY plane of `g`, w x h px, top-left at (x, y), mapped to the uv rect given in 0..1. */
function quad(mat: THREE.Material, x: number, y: number, w: number, h: number, u0: number, v0: number, u1: number, v1: number): THREE.Mesh {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute([x, y, 0, x + w, y, 0, x + w, y + h, 0, x, y + h, 0], 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute([0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1], 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute([u0, v0, u1, v0, u1, v1, u0, v1], 2));
  geo.setIndex([0, 1, 2, 0, 2, 3]);
  const m = new THREE.Mesh(geo, mat);
  m.frustumCulled = false;
  return m;
}

/** Two crossed quads of one whole block texture (a mushroom, a beam segment), 16 px, standing on y = 0 of `g`. */
function cross(mat: THREE.Material, size = 16): THREE.Group {
  const g = new THREE.Group();
  for (const ry of [PI / 4, -PI / 4]) {
    const q = quad(mat, -size / 2, -size, size, size, 0, 0, 1, 1);
    q.rotation.y = ry;
    g.add(q);
  }
  return g;
}

// ------------------------------------------------------------------ rabbit

const RABBIT_SHEETS = ['rabbit_brown', 'rabbit_white', 'rabbit_black', 'rabbit_white_splotched', 'rabbit_gold', 'rabbit_salt'] as const;

/** RabbitModel (64x64, the 26.1 sheet): body, head with two ears, tail, front legs and long hind feet. */
export function rabbitAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/rabbit/rabbit_brown', 64, 64);
  const body = r.pivot([0, 21, 4], r.parts, [-0.2, 0, 0]);
  r.box(body, [0, 0], [-4, -6, -10], [8, 6, 10]);
  const tail = r.pivot([0, -5, -1], body);
  r.box(tail, [20, 16], [-2, -2, 0], [4, 4, 4], -0.6);
  const head = r.pivot([0, 15, -5]);
  r.box(head, [0, 16], [-2.5, -4, -4], [5, 5, 5]);
  const earR = r.pivot([-1.5, -4, 0], head, [0, 0, -0.15]), earL = r.pivot([1.5, -4, 0], head, [0, 0, 0.15]);
  r.box(earR, [26, 0], [-1, -5, -0.5], [2, 5, 1]);
  r.box(earL, [32, 0], [-1, -5, -0.5], [2, 5, 1]);
  const fr = r.pivot([-2.5, 20, -4]), fl = r.pivot([2.5, 20, -4]);
  r.box(fr, [36, 18], [-1, 0, -1], [2, 4, 2]);
  r.box(fl, [44, 18], [-1, 0, -1], [2, 4, 2]);
  const hr = r.pivot([-3, 23, 4]), hl = r.pivot([3, 23, 4]);
  r.box(hr, [20, 24], [-1, 0, -5], [2, 1, 6]);
  r.box(hl, [36, 24], [-1, 0, -5], [2, 1, 6]);
  let coat = 0;
  return r.anim((v, q, headY, headX) => {
    const c = Math.min(RABBIT_SHEETS.length - 1, Math.max(0, v.color));
    if (c !== coat) { coat = c; r.mat.uniforms.uMap.value = sheetTexture(`entity/rabbit/${RABBIT_SHEETS[c]}`); }
    babyScale(r.parts, v.baby);
    head.rotation.set(headX, headY, 0);
    // the hop: hind feet kick back and the front legs reach forward while it is off the ground
    const hop = q.airborne || q.limbAmt > 0.1 ? Math.abs(Math.sin(q.limbSwing * 0.6662)) * Math.min(1, q.limbAmt * 2) : 0;
    hr.rotation.x = hl.rotation.x = hop * 0.9;
    fr.rotation.x = fl.rotation.x = -hop * 0.7;
    body.rotation.x = -0.2 - hop * 0.15;
  });
}

// -------------------------------------------------------------------- goat

/** GoatModel (64x64). `view.color` bits 0-1 horn count, bits 2-3 ram state (1 lowering, 2 charging). */
export function goatAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/goat/goat', 64, 64);
  const head = r.pivot([1, 14, 0]);
  r.box(head, [2, 61], [-6, -11, -10], [3, 2, 1]);
  r.box(head, [2, 61], [2, -11, -10], [3, 2, 1], 0, true);
  r.box(head, [23, 52], [0, -3, -14], [0, 7, 5]);
  const hornL = r.pivot([0, 0, 0], head), hornR = r.pivot([0, 0, 0], head);
  r.box(hornL, [12, 55], [-0.01, -16, -10], [2, 7, 2]);
  r.box(hornR, [12, 55], [-2.99, -16, -10], [2, 7, 2]);
  const nose = r.pivot([0, -8, -8], head, [0.9599, 0, 0]);
  r.box(nose, [34, 46], [-3, -4, -8], [5, 7, 10]);
  const body = r.pivot([0, 24, 0]);
  r.box(body, [1, 1], [-4, -17, -7], [9, 11, 16]);
  r.box(body, [0, 28], [-5, -18, -8], [11, 14, 11]);
  const lh = r.pivot([1, 14, 4]), rh = r.pivot([-3, 14, 4]), lf = r.pivot([1, 14, -6]), rf = r.pivot([-3, 14, -6]);
  r.box(lh, [36, 29], [0, 4, 0], [3, 6, 3]);
  r.box(rh, [49, 29], [0, 4, 0], [3, 6, 3]);
  r.box(lf, [49, 2], [0, 0, 0], [3, 10, 3]);
  r.box(rf, [35, 2], [0, 0, 0], [3, 10, 3]);
  let lower = 0;
  return r.anim((v, q, headY, headX) => {
    babyScale(r.parts, v.baby);
    const horns = v.color & 3, ram = (v.color >> 2) & 3;
    hornL.visible = horns >= 2 && !v.baby; hornR.visible = horns >= 1 && !v.baby;
    // Goat.getRammingXHeadRot: the head tips 30 degrees down while it lowers / charges
    lower += ((ram > 0 ? 1 : 0) - lower) * 0.2;
    head.rotation.set(headX + lower * (PI / 6) * 1.7, headY, 0);
    const s = q.limbSwing * 0.6662, a = q.limbAmt * 1.4;
    rh.rotation.x = Math.cos(s) * a; lh.rotation.x = Math.cos(s + PI) * a;
    rf.rotation.x = Math.cos(s + PI) * a; lf.rotation.x = Math.cos(s) * a;
  });
}

// ------------------------------------------------------------------ turtle

/** TurtleModel (128x64): shell + belly plate, head, four flippers. */
export function turtleAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/turtle/turtle', 128, 64);
  const head = r.pivot([0, 19, -10]);
  r.box(head, [3, 0], [-3, -1, -3], [6, 5, 6]);
  const body = r.pivot([0, 11, -10], r.parts, [PI / 2, 0, 0]);
  r.box(body, [7, 37], [-9.5, 3, -10], [19, 20, 6]);
  r.box(body, [31, 1], [-5.5, 3, -13], [11, 18, 3]);
  const rh = r.pivot([-3.5, 22, 11]), lh = r.pivot([3.5, 22, 11]);
  r.box(rh, [1, 23], [-2, 0, 0], [4, 1, 10]);
  r.box(lh, [1, 12], [-2, 0, 0], [4, 1, 10]);
  const rf = r.pivot([-5, 21, -4]), lf = r.pivot([5, 21, -4]);
  r.box(rf, [27, 30], [-13, 0, -2], [13, 1, 5]);
  r.box(lf, [27, 24], [0, 0, -2], [13, 1, 5]);
  return r.anim((v, q, headY, headX) => {
    babyScale(r.parts, v.baby);
    head.rotation.set(headX, headY, 0);
    // TurtleModel.setupAnim, on land: the front flippers row, the hind ones paddle sideways
    const s = q.limbSwing, a = q.limbAmt;
    const f = Math.cos(s * 5) * 8 * a * 0.5;
    rf.rotation.set(0, -f, 0); lf.rotation.set(0, f, 0);
    rf.rotation.z = -Math.cos(s * 0.6662 * 0.6) * 0.5 * a; lf.rotation.z = Math.cos(s * 0.6662 * 0.6) * 0.5 * a;
    rh.rotation.y = Math.cos(s * 5 + PI) * 3 * a * 0.3; lh.rotation.y = Math.cos(s * 5) * 3 * a * 0.3;
  });
}

// --------------------------------------------------------------- mooshroom

/** CowModel over the red mooshroom sheet, with the three red mushrooms (two on the back, one on the head). */
export function mooshroomAnim(env: EnvUniforms): OwAnim {
  const m = new CowModel(env);
  const sheet = m.mats[0];
  sheet.uniforms.uMap.value = sheetTexture('entity/cow/mooshroom_red');
  const shroomMat = entityMaterial(env, sheetTexture('red_mushroom'), { doubleSided: true });
  const shrooms = new THREE.Group();
  const at = (x: number, y: number, z: number, parent: THREE.Object3D): void => {
    const c = cross(shroomMat);
    c.position.set(x, y, z);
    parent.add(c);
  };
  // MushroomCowMushroomLayer: on the back at the shoulders and the rump, and on top of the head
  at(-2, 3, -3, shrooms); at(1, 3, 7, shrooms);
  m.parts.add(shrooms);
  const head = (m as unknown as { head: THREE.Group }).head;
  const headShroom = new THREE.Group();
  at(0, -4, -3, headShroom);
  head.add(headShroom);
  let bare = false;
  return {
    parts: m.parts, mats: [...m.mats, shroomMat],
    animate(v, q) {
      babyScale(m.parts, v.baby);
      if (v.sheared !== bare) {
        bare = v.sheared;
        sheet.uniforms.uMap.value = sheetTexture(bare ? 'entity/cow/cow_temperate' : 'entity/cow/mooshroom_red');
      }
      shrooms.visible = headShroom.visible = !bare && !v.baby;
      m.animate(q);
    },
    dispose() { for (const x of m.mats) x.dispose(); shroomMat.dispose(); },
  };
}

// -------------------------------------------------------------- polar bear

/** PolarBearModel (128x64). `view.color` bit 0: standing up to attack. */
export function polarBearAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/bear/polarbear', 128, 64);
  const head = r.pivot([0, 10, -16]);
  r.box(head, [0, 0], [-3.5, -3, -3], [7, 7, 7]);
  r.box(head, [0, 44], [-2.5, 1, -6], [5, 3, 3]);
  r.box(head, [26, 0], [-4.5, -4, -1], [2, 2, 1]);
  r.box(head, [26, 0], [2.5, -4, -1], [2, 2, 1], 0, true);
  const body = r.pivot([-2, 9, 12], r.parts, [PI / 2, 0, 0]);
  r.box(body, [0, 19], [-5, -13, -7], [14, 14, 11]);
  r.box(body, [39, 0], [-4, -25, -7], [12, 12, 10]);
  const rh = r.pivot([-4.5, 14, 6]), lh = r.pivot([4.5, 14, 6]);
  r.box(rh, [50, 22], [-2, 0, -2], [4, 10, 8]);
  r.box(lh, [50, 22], [-2, 0, -2], [4, 10, 8]);
  const rf = r.pivot([-3.5, 14, -8]), lf = r.pivot([3.5, 14, -8]);
  r.box(rf, [50, 40], [-2, 0, -2], [4, 10, 6]);
  r.box(lf, [50, 40], [-2, 0, -2], [4, 10, 6]);
  let stand = 0;
  return r.anim((v, q, headY, headX) => {
    babyScale(r.parts, v.baby);
    stand += (((v.color & 1) !== 0 ? 1 : 0) - stand) * 0.15;
    const s = q.limbSwing * 0.6662, a = q.limbAmt * 1.4;
    rh.rotation.x = Math.cos(s) * a; lh.rotation.x = Math.cos(s + PI) * a;
    rf.rotation.x = Math.cos(s + PI) * a; lf.rotation.x = Math.cos(s) * a;
    // PolarBearModel.setupAnim standing pose
    const f = stand * stand, g = 1 - f;
    body.rotation.x = PI / 2 - f * PI * 0.35;
    body.position.y = 9 * g + 11 * f;
    rf.position.y = lf.position.y = 14 * g - 6 * f;
    rf.position.z = lf.position.z = -8 * g - 4 * f;
    rf.rotation.x -= f * PI * 0.45; lf.rotation.x -= f * PI * 0.45;
    head.position.y = 10 * g - 12 * f;
    head.position.z = -16 * g - 3 * f;
    head.rotation.set(headX + f * PI * 0.15, headY, 0);
  });
}

// ------------------------------------------------------------------ ocelot

/** OcelotModel (64x32), the cat model over the ocelot coat. */
export function ocelotAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/cat/ocelot', 64, 32);
  const head = r.pivot([0, 15, -9]);
  r.box(head, [0, 0], [-2.5, -2, -3], [5, 4, 5]);
  r.box(head, [0, 24], [-1.5, -0.001, -4], [3, 2, 2]);
  r.box(head, [0, 10], [-2, -3, 0], [1, 1, 2]);
  r.box(head, [6, 10], [1, -3, 0], [1, 1, 2]);
  const body = r.pivot([0, 12, -10], r.parts, [PI / 2, 0, 0]);
  r.box(body, [20, 0], [-2, 3, -8], [4, 16, 6]);
  const tail1 = r.pivot([0, 15, 8], r.parts, [0.9, 0, 0]);
  r.box(tail1, [0, 15], [-0.5, 0, 0], [1, 8, 1]);
  const tail2 = r.pivot([0, 20, 14]);
  r.box(tail2, [4, 15], [-0.5, 0, 0], [1, 8, 1], -0.02);
  const bl = r.pivot([1.1, 18, 5]), br = r.pivot([-1.1, 18, 5]);
  r.box(bl, [8, 13], [-1, 0, 1], [2, 6, 2]); r.box(br, [8, 13], [-1, 0, 1], [2, 6, 2]);
  const fl = r.pivot([1.2, 14.1, -5]), fr = r.pivot([-1.2, 14.1, -5]);
  r.box(fl, [40, 0], [-1, 0, 0], [2, 10, 2]); r.box(fr, [40, 0], [-1, 0, 0], [2, 10, 2]);
  return r.anim((v, q, headY, headX, age) => {
    babyScale(r.parts, v.baby);
    head.rotation.set(headX, headY, 0);
    const s = q.limbSwing * 0.6662, a = q.limbAmt;
    fr.rotation.x = Math.cos(s) * a; fl.rotation.x = Math.cos(s + 0.3) * a;
    br.rotation.x = Math.cos(s + PI + 0.3) * a; bl.rotation.x = Math.cos(s + PI) * a;
    tail1.rotation.x = 0.9 - a * 0.3; tail2.rotation.x = Math.sin(age * 0.1) * 0.05;
  });
}

// -------------------------------------------------------------- snow golem

/** SnowGolemModel (64x64) + the pumpkin on its head (hidden once sheared). */
export function snowGolemAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/snow_golem/snow_golem', 64, 64);
  const head = r.pivot([0, 4, 0]);
  r.box(head, [0, 0], [-4, -8, -4], [8, 8, 8], -0.5);
  const armL = r.pivot([5, 6, 1], r.parts, [0, 0, 1]);
  r.box(armL, [32, 0], [-1, 0, -1], [12, 2, 2], -0.5);
  const armR = r.pivot([-5, 6, -1], r.parts, [0, PI, -1]);
  r.box(armR, [32, 0], [-1, 0, -1], [12, 2, 2], -0.5);
  const upper = r.pivot([0, 13, 0]);
  r.box(upper, [0, 16], [-5, -10, -5], [10, 10, 10], -0.5);
  const lower = r.pivot([0, 24, 0]);
  r.box(lower, [0, 36], [-6, -12, -6], [12, 12, 12], -0.5);
  // SnowGolemHeadLayer: a pumpkin block at 0.625 scale (10 px) over the head
  const side = entityMaterial(env, sheetTexture('pumpkin_side')), top = entityMaterial(env, sheetTexture('pumpkin_top'));
  const pumpkin = new THREE.Mesh(new THREE.BoxGeometry(10, 10, 10), [side, side, top, top, side, side]);
  pumpkin.position.set(0, -4, 0);
  pumpkin.frustumCulled = false;
  head.add(pumpkin);
  r.mats.push(side, top);
  return r.anim((v, _q, headY, headX) => {
    head.rotation.set(headX, headY, 0);
    pumpkin.visible = !v.sheared;
    // SnowGolemModel.setupAnim: the chest turns a quarter of the way with the head and carries the arms
    const t = headY * 0.25;
    upper.rotation.y = t;
    const sn = Math.sin(t), cs = Math.cos(t);
    armL.rotation.y = t; armR.rotation.y = t + PI;
    armL.position.x = cs * 5; armL.position.z = -sn * 5;
    armR.position.x = -cs * 5; armR.position.z = sn * 5;
    // throwing: the arms jerk up with the swing
    const sw = v.attackAnim > 0 ? Math.sin(v.attackAnim * PI) * 0.6 : 0;
    armL.rotation.z = 1 - sw; armR.rotation.z = -1 + sw;
  });
}

// ----------------------------------------------------------------- axolotl

const AXOLOTL_SHEETS = ['axolotl_lucy', 'axolotl_wild', 'axolotl_gold', 'axolotl_cyan', 'axolotl_blue'] as const;

/** AxolotlModel (64x64). `view.color` bits 0-2 variant, bit 3 playing dead. */
export function axolotlAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/axolotl/axolotl_lucy', 64, 64, { doubleSided: true });
  const body = r.pivot([0, 20, 5]);
  r.box(body, [0, 11], [-4, -2, -9], [8, 4, 10]);
  r.box(body, [2, 17], [0, -3, -8], [0, 5, 9]);
  const head = r.pivot([0, 0, -9], body);
  r.box(head, [0, 1], [-4, -3, -5], [8, 5, 5]);
  const gillT = r.pivot([0, -3, -1], head), gillL = r.pivot([-4, 0, -1], head), gillR = r.pivot([4, 0, -1], head);
  r.box(gillT, [3, 37], [-4, -3, 0], [8, 3, 0]);
  r.box(gillL, [0, 40], [-3, -5, 0], [3, 7, 0]);
  r.box(gillR, [11, 40], [0, -5, 0], [3, 7, 0]);
  const legs = [
    r.pivot([-3.5, 1, -1], body), r.pivot([3.5, 1, -1], body),
    r.pivot([-3.5, 1, -8], body), r.pivot([3.5, 1, -8], body),
  ];
  r.box(legs[0], [2, 13], [-1, 0, 0], [3, 5, 0]);
  r.box(legs[1], [2, 13], [-2, 0, 0], [3, 5, 0]);
  r.box(legs[2], [2, 13], [-1, 0, 0], [3, 5, 0]);
  r.box(legs[3], [2, 13], [-2, 0, 0], [3, 5, 0]);
  const tail = r.pivot([0, 0, 1], body);
  r.box(tail, [2, 19], [0, -3, 0], [0, 5, 12]);
  let variant = 0;
  return r.anim((v, q, headY, headX, age) => {
    const c = Math.min(AXOLOTL_SHEETS.length - 1, v.color & 7);
    if (c !== variant) { variant = c; r.mat.uniforms.uMap.value = sheetTexture(`entity/axolotl/${AXOLOTL_SHEETS[c]}`); }
    babyScale(r.parts, v.baby);
    const dead = (v.color & 8) !== 0;
    head.rotation.set(headX * 0.5, headY * 0.5, 0);
    if (dead) {
      // AxolotlModel.setupPlayDeadAnimation: flat on the bottom, legs splayed, gills limp
      body.rotation.set(0, 0, 0);
      legs[0].rotation.set(1.4, -1.1, 0); legs[1].rotation.set(1.4, 1.1, 0);
      legs[2].rotation.set(-1.4, 1.1, 0); legs[3].rotation.set(-1.4, -1.1, 0);
      tail.rotation.y = 0.5;
      gillT.rotation.x = 0; gillL.rotation.y = 0; gillR.rotation.y = 0;
      return;
    }
    const moving = Math.min(1, q.limbAmt * 3);
    const t = age * (0.11 + moving * 0.22);
    // swimming: legs trail back along the body and the tail sculls; idle: legs hang and paddle
    body.rotation.set(-headX * moving, 0, Math.sin(t) * 0.09 * moving);
    const trail = 1.88 * moving, hang = (1 - moving);
    const pad = Math.sin(age * 0.09) * 0.2 * hang;
    legs[0].rotation.set(trail + 0.6 * hang + pad, 0, -0.35 * hang);
    legs[1].rotation.set(trail + 0.6 * hang - pad, 0, 0.35 * hang);
    legs[2].rotation.set(trail + 0.6 * hang - pad, 0, -0.35 * hang);
    legs[3].rotation.set(trail + 0.6 * hang + pad, 0, 0.35 * hang);
    tail.rotation.y = Math.sin(t) * (0.3 + 0.2 * moving);
    const gill = Math.sin(age * 0.09) * 0.13;
    gillT.rotation.x = 0.6 + gill; gillL.rotation.y = -0.6 - gill; gillR.rotation.y = 0.6 + gill;
  });
}

// ----------------------------------------------------------------- dolphin

/** DolphinModel (64x64): body carrying the head + nose, back and side fins, tail + fluke. */
export function dolphinAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/dolphin/dolphin', 64, 64);
  const body = r.pivot([0, 22, -5]);
  r.box(body, [22, 0], [-4, -7, 0], [8, 7, 13]);
  const back = r.pivot([0, 0, 0], body, [PI / 3, 0, 0]);
  r.box(back, [51, 0], [-0.5, 0, 8], [1, 4, 5]);
  const finL = r.pivot([2, -2, 4], body, [PI / 3, 0, 2.0944]);
  r.box(finL, [48, 20], [-0.5, -4, 0], [1, 4, 7], 0, true);
  const finR = r.pivot([-2, -2, 4], body, [PI / 3, 0, -2.0944]);
  r.box(finR, [48, 20], [-0.5, -4, 0], [1, 4, 7]);
  const tail = r.pivot([0, -2.5, 11], body, [-0.1047, 0, 0]);
  r.box(tail, [0, 19], [-2, -2.5, 0], [4, 5, 11]);
  const fluke = r.pivot([0, 0, 9], tail);
  r.box(fluke, [19, 20], [-5, -0.5, 0], [10, 1, 6]);
  const head = r.pivot([0, -4, -3], body);
  r.box(head, [0, 0], [-4, -3, -3], [8, 7, 6]);
  r.box(head, [0, 13], [-1, 2, -7], [2, 2, 4]);
  return r.anim((_v, q, headY, headX, age) => {
    // DolphinModel.setupAnim: the whole body pitches with the look, the tail beats while it moves
    body.rotation.x = headX; body.rotation.y = headY * 0.3;
    const moving = q.limbAmt > 0.02 ? 1 : 0.3;
    tail.rotation.x = -0.1047 - 0.1 * Math.cos(age * 0.3) * moving;
    fluke.rotation.x = -0.2 * Math.cos(age * 0.3) * moving;
  });
}

// ---------------------------------------------------------------- guardian

const SPIKE_X = [1.75, 0.25, 0, 0, 0.5, 0.5, 0.5, 0.5, 1.25, 0.75, 0, 0];
const SPIKE_Y = [0, 0, 0, 0, 0.25, 1.75, 1.25, 0.75, 0, 0, 0, 0];
const SPIKE_Z = [0, 0, 0.25, 1.75, 0, 0, 0, 0, 0, 0, 0.75, 1.25];
const SPIKE_PX = [0, 0, 8, -8, -8, 8, 8, -8, 0, 0, 8, -8];
const SPIKE_PY = [-8, -8, -8, -8, 0, 0, 0, 0, 8, 8, 8, 8];
const SPIKE_PZ = [8, -8, 0, 0, -8, -8, 8, 8, 8, -8, 0, 0];
/** The elder guardian is the guardian model at this scale (ElderGuardianRenderer). */
const ELDER_SCALE = 2.35;
const BEAM_SEGMENTS = 16;

/**
 * GuardianModel (64x64); `elder` draws it 2.35x over the elder sheet. `view.color` is the beam
 * length in quarter blocks (0 = no beam) and `view.swell` its charge 0..1 (entities/mobs3/water.ts).
 */
export function guardianAnim(env: EnvUniforms, elder: boolean): OwAnim {
  const r = new Rig(env, elder ? 'entity/guardian/guardian_elder' : 'entity/guardian/guardian', 64, 64);
  const head = r.pivot([0, 0, 0]);
  r.box(head, [0, 0], [-6, 10, -8], [12, 12, 16]);
  r.box(head, [0, 28], [-8, 10, -6], [2, 12, 12]);
  r.box(head, [0, 28], [6, 10, -6], [2, 12, 12], 0, true);
  r.box(head, [16, 40], [-6, 8, -6], [12, 2, 12]);
  r.box(head, [16, 40], [-6, 22, -6], [12, 2, 12]);
  const spikes: THREE.Group[] = [];
  for (let i = 0; i < 12; i++) {
    const s = r.pivot([SPIKE_PX[i], 16 + SPIKE_PY[i], SPIKE_PZ[i]], head, [PI * SPIKE_X[i], PI * SPIKE_Y[i], PI * SPIKE_Z[i]]);
    r.box(s, [0, 0], [-1, -4.5, -1], [2, 9, 2]);
    spikes.push(s);
  }
  const eye = r.pivot([0, 0, -8.25], head);
  r.box(eye, [8, 0], [-1, 15, 0], [2, 2, 1]);
  const tail0 = r.pivot([0, 0, 0], head);
  r.box(tail0, [40, 0], [-2, 14, 7], [4, 4, 8]);
  const tail1 = r.pivot([-1.5, 0.5, 14], tail0);
  r.box(tail1, [0, 54], [0, 14, 0], [3, 3, 7]);
  const tail2 = r.pivot([0.5, 0.5, 6], tail1);
  r.box(tail2, [41, 32], [0, 14, 0], [2, 2, 6]);
  r.box(tail2, [25, 19], [1, 10.5, 3], [1, 9, 9]);
  // the beam: one block-long crossed segment per block of length, from the eye along the look
  const beamMat = entityMaterial(env, sheetTexture('entity/guardian/guardian_beam'), { emissive: true, doubleSided: true });
  const beam = new THREE.Group();
  beam.position.set(0, 16, 0);
  const segs: THREE.Object3D[] = [];
  for (let i = 0; i < BEAM_SEGMENTS; i++) {
    const seg = new THREE.Group();
    for (const rz of [0, PI / 2]) {
      const q = quad(beamMat, -3.2, 0, 6.4, 16, 0, 0, 0.5, 1);
      // lay the quad along -z: its y axis becomes the beam axis
      const holder = new THREE.Group();
      holder.rotation.z = rz;
      q.rotation.x = -PI / 2;
      holder.add(q);
      seg.add(holder);
    }
    seg.position.z = -8 - i * 16;
    beam.add(seg);
    segs.push(seg);
  }
  r.parts.add(beam);
  r.mats.push(beamMat);
  const scale = elder ? ELDER_SCALE : 1;
  const tint = new THREE.Color();
  return r.anim((v, q, _headY, headX, age) => {
    babyScale(r.parts, false, scale);
    // the cube centre is 8 px above the feet; pitch the whole head about it
    pitchAbout(head, headX);
    // GuardianModel.setupAnim: spikes pulse out while idle and pull in while it swims
    const out = 1 - Math.min(1, q.limbAmt * 4);
    for (let i = 0; i < 12; i++) {
      const k = 1 + Math.cos(age * 1.5 + i) * 0.01 - (1 - out) * 0.55;
      spikes[i].position.set(SPIKE_PX[i] * k, 16 + SPIKE_PY[i] * k, SPIKE_PZ[i] * k);
    }
    const wag = Math.sin(age * 0.35) * PI * (0.02 + 0.03 * (1 - out));
    tail0.rotation.y = wag; tail1.rotation.y = wag * 2; tail2.rotation.y = wag * 3;
    // beam: yellow-to-white as the charge builds (GuardianRenderer colour ramp)
    const len = v.deathTime > 0 ? 0 : v.color / 4 / scale;
    beam.visible = len > 0;
    if (len > 0) {
      beam.rotation.x = headX;
      const f = v.swell * v.swell;
      tint.setRGB((64 + f * 191) / 255, (32 + f * 191) / 255, (128 - f * 64) / 255);
      beamMat.uniforms.uTint.value.copy(tint);
      for (let i = 0; i < BEAM_SEGMENTS; i++) {
        const left = len - 0.5 - i;
        segs[i].visible = left > 0;
        segs[i].scale.z = Math.min(1, left);
      }
    }
  });
}

/** Pitch a part whose boxes are authored around y = 16 about that centre. */
function pitchAbout(head: THREE.Group, pitch: number): void {
  // boxes sit at y 8..24 in the part's own space: shift the pivot to the centre, rotate, shift back
  const c = Math.cos(pitch), s = Math.sin(pitch);
  head.rotation.x = pitch;
  // p' = R (p - C) + C with C = (0, 16, 0)  =>  translation = C - R C
  head.position.set(0, 16 - 16 * c, -16 * s);
}

// ----------------------------------------------------------------- shulker

/** ShulkerModel (64x64): base, lid and the head inside. `view.color` 0 shut, 1 peeking, 2 open. */
export function shulkerAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/shulker/shulker', 64, 64);
  const lid = r.pivot([0, 24, 0]);
  r.box(lid, [0, 0], [-8, -16, -8], [16, 12, 16]);
  const base = r.pivot([0, 24, 0]);
  r.box(base, [0, 28], [-8, -8, -8], [16, 8, 16]);
  const head = r.pivot([0, 12, 0]);
  r.box(head, [0, 52], [-3, 0, -3], [6, 6, 6]);
  let peek = 0;
  return r.anim((v, _q, headY, headX) => {
    const want = v.color >= 2 ? 1 : v.color === 1 ? 0.3 : 0;
    peek += (want - peek) * 0.2;
    // ShulkerModel.setupAnim
    const f = (0.5 + peek) * PI;
    const g = -1 + Math.sin(f);
    const lift = f > PI ? Math.sin(v.id + performance.now() / 1000 * 0.02) * 0.7 : 0;
    lid.position.y = 16 + Math.sin(f) * 8 + lift;
    lid.rotation.y = peek > 0.3 ? g * g * g * g * PI * 0.125 : 0;
    head.rotation.set(headX, headY, 0);
    head.visible = peek > 0.02;
  });
}

/** ShulkerBulletModel (spark sheet 64x32): three crossed slabs tumbling about the centre, drawn at half scale. */
export function shulkerBulletAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/shulker/spark', 64, 32);
  const main = r.pivot([0, 21.5, 0]);
  r.box(main, [0, 0], [-4, -4, -1], [8, 8, 2]);
  r.box(main, [0, 10], [-1, -4, -4], [2, 8, 8]);
  r.box(main, [20, 0], [-4, -1, -4], [8, 2, 8]);
  main.scale.setScalar(0.5);
  return r.anim((_v, _q, _hy, _hx, age) => {
    // ShulkerBulletRenderer: spins on all three axes
    main.rotation.set(Math.sin(age * 0.15) * PI, Math.sin(age * 0.1) * PI, Math.cos(age * 0.03) * PI);
  });
}

// ------------------------------------------------------------------ evoker

/** IllagerModel (64x64) over the evoker sheet. `view.color` != 0 while casting: both arms raised and waving. */
export function evokerAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/illager/evoker', 64, 64);
  const head = r.pivot([0, 0, 0]);
  r.box(head, [0, 0], [-4, -10, -4], [8, 10, 8]);
  const nose = r.pivot([0, -2, 0], head);
  r.box(nose, [24, 0], [-1, -1, -6], [2, 4, 2]);
  const body = r.pivot([0, 0, 0]);
  r.box(body, [16, 20], [-4, 0, -3], [8, 12, 6]);
  r.box(body, [0, 38], [-4, 0, -3], [8, 20, 6], 0.5);
  const arms = r.pivot([0, 3, -1], r.parts, [-0.75, 0, 0]);
  r.box(arms, [44, 22], [-8, -2, -2], [4, 8, 4]);
  r.box(arms, [44, 22], [4, -2, -2], [4, 8, 4], 0, true);
  r.box(arms, [40, 38], [-4, 2, -2], [8, 4, 4]);
  const legR = r.pivot([-2, 12, 0]), legL = r.pivot([2, 12, 0]);
  r.box(legR, [0, 22], [-2, 0, -2], [4, 12, 4]);
  r.box(legL, [0, 22], [-2, 0, -2], [4, 12, 4], 0, true);
  const armR = r.pivot([-5, 2, 0]), armL = r.pivot([5, 2, 0]);
  r.box(armR, [40, 46], [-3, -2, -2], [4, 12, 4]);
  r.box(armL, [40, 46], [-1, -2, -2], [4, 12, 4], 0, true);
  return r.anim((v, q, headY, headX, age) => {
    head.rotation.set(headX, headY, 0);
    const s = q.limbSwing * 0.6662, a = q.limbAmt;
    legR.rotation.x = Math.cos(s) * 1.4 * a * 0.5;
    legL.rotation.x = Math.cos(s + PI) * 1.4 * a * 0.5;
    const casting = v.color !== 0 && v.deathTime === 0;
    arms.visible = !casting; armR.visible = armL.visible = casting;
    if (casting) {
      // IllagerModel SPELLCASTING pose
      armR.position.set(-5, 2, 0); armL.position.set(5, 2, 0);
      armR.rotation.set(Math.cos(age * 0.6662) * 0.25, 0, 2.3561945);
      armL.rotation.set(Math.cos(age * 0.6662) * 0.25, 0, -2.3561945);
    }
  });
}

/**
 * EvokerFangsModel (64x32). `view.color` 0 while it waits underground, `view.swell` the bite
 * progress 0..1: the jaws rise out of the ground, snap shut and shrink away.
 */
export function evokerFangsAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/illager/evoker_fangs', 64, 32);
  // EvokerFangsRenderer draws the model 0.626 * 2 blocks up at net scale 1: its y = 24 is 4 px under the ground
  const root = r.pivot([0, 4, 0]);
  const base = r.pivot([-5, 24, -5], root);
  r.box(base, [0, 0], [0, 0, 0], [10, 12, 10]);
  const upper = r.pivot([1.5, 24, -4], root);
  r.box(upper, [40, 0], [0, 0, 0], [4, 14, 8]);
  const lower = r.pivot([-1.5, 24, 4], root, [0, PI, 0]);
  r.box(lower, [40, 0], [0, 0, 0], [4, 14, 8]);
  return r.anim((v) => {
    const p = v.swell;
    r.parts.visible = p > 0;
    let f = Math.min(p * 2, 1);
    f = 1 - f * f * f;
    upper.rotation.z = PI - f * 0.35 * PI;
    lower.rotation.z = PI + f * 0.35 * PI;
    const g = (p + Math.sin(p * 2.7)) * 0.6 * 12;
    upper.position.y = lower.position.y = base.position.y = 24 - g;
    // the last tenth of the bite shrinks to nothing
    const k = p > 0.9 ? Math.max(0.001, (1 - p) / 0.1) : 1;
    root.scale.setScalar(k);
    root.position.y = 4 + 24 * (1 - k);
  });
}

// --------------------------------------------------------------------- vex

/** VexModel (32x32) with its iron sword; the charging sheet while it attacks. */
export function vexAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/illager/vex', 32, 32, { doubleSided: true });
  const root = r.pivot([0, -2.5, 0]);
  const head = r.pivot([0, 20, 0], root);
  r.box(head, [0, 0], [-2.5, -5, -2.5], [5, 5, 5]);
  const body = r.pivot([0, 20, 0], root);
  r.box(body, [0, 10], [-1.5, 0, -1], [3, 4, 2]);
  r.box(body, [0, 16], [-1.5, 1, -1], [3, 5, 2], -0.2);
  const armR = r.pivot([-1.75, 0.25, 0], body), armL = r.pivot([1.75, 0.25, 0], body);
  r.box(armR, [23, 0], [-1.25, -0.5, -1], [2, 4, 2], -0.1);
  r.box(armL, [23, 6], [-0.75, -0.5, -1], [2, 4, 2], -0.1);
  const wingL = r.pivot([0.5, 1, 1], body), wingR = r.pivot([-0.5, 1, 1], body);
  r.box(wingL, [16, 14], [0, 0, 0], [0, 5, 8], 0, true);
  r.box(wingR, [16, 14], [0, 0, 0], [0, 5, 8]);
  const sword = itemSpriteMesh(env, 'iron_sword');
  applyHandheld(sword.mesh, 'handheld');
  const hand = new THREE.Group();
  hand.rotation.order = 'XYZ';
  hand.rotation.set(-PI / 2, PI, 0);
  hand.position.set(-0.25, 3.5, -1);
  hand.scale.setScalar(8); // the vex and its sword are drawn at about half a humanoid's size
  hand.add(sword.mesh);
  armR.add(hand);
  r.mats.push(sword.mat);
  let charging = false;
  return r.anim((v, _q, headY, headX, age) => {
    if (v.aggressive !== charging) {
      charging = v.aggressive;
      r.mat.uniforms.uMap.value = sheetTexture(charging ? 'entity/illager/vex_charging' : 'entity/illager/vex');
    }
    sword.mesh.visible = v.heldItem !== 0;
    head.rotation.set(headX, headY, 0);
    // VexModel.setupAnim
    const flap = Math.cos(age * 0.8) * 0.283;
    body.rotation.x = charging ? 0 : 0.6283;
    wingR.rotation.set(0.4712, -(1.0996 + flap), -0.4712);
    wingL.rotation.set(0.4712, 1.0996 + flap, 0.4712);
    if (charging) {
      armR.rotation.set(-1.2217, 0.2618, -0.4712);
      armL.rotation.set(-1.2217, -0.2618, 0.4712);
    } else {
      const sway = Math.cos(age * 0.09) * 0.05;
      armR.rotation.set(-0.6283 + (v.attackAnim > 0 ? -Math.sin(v.attackAnim * PI) * 1.2 : 0), 0, 0.3 + sway);
      armL.rotation.set(-0.6283, 0, -0.3 - sway);
    }
    root.position.y = -2.5 + Math.sin(age * 0.1) * 0.5;
  });
}

// ----------------------------------------------------------------- ravager

/** RavagerModel (128x128). `view.color` 1 while it roars (mouth open). */
export function ravagerAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/illager/ravager', 128, 128);
  const neck = r.pivot([0, -7, 5.5]);
  r.box(neck, [68, 73], [-5, -1, -18], [10, 10, 18]);
  const head = r.pivot([0, 16, -17], neck);
  r.box(head, [0, 0], [-8, -20, -14], [16, 20, 16]);
  r.box(head, [0, 0], [-2, -6, -18], [4, 8, 4]);
  const hornR = r.pivot([-10, -14, -8], head, [1.0996, 0, 0]);
  r.box(hornR, [74, 55], [0, -14, -2], [2, 14, 4]);
  const hornL = r.pivot([8, -14, -8], head, [1.0996, 0, 0]);
  r.box(hornL, [74, 55], [0, -14, -2], [2, 14, 4], 0, true);
  const mouth = r.pivot([0, -2, 2], head);
  r.box(mouth, [0, 36], [-8, 0, -16], [16, 3, 16]);
  const body = r.pivot([0, 1, 2], r.parts, [PI / 2, 0, 0]);
  r.box(body, [0, 55], [-7, -10, -7], [14, 16, 20]);
  r.box(body, [0, 91], [-6, 6, -7], [12, 13, 18]);
  const rh = r.pivot([-8, -13, 18]), lh = r.pivot([8, -13, 18]);
  r.box(rh, [96, 0], [-4, 0, -4], [8, 37, 8]);
  r.box(lh, [96, 0], [-4, 0, -4], [8, 37, 8], 0, true);
  const rf = r.pivot([-8, -13, -5]), lf = r.pivot([8, -13, -5]);
  r.box(rf, [64, 0], [-4, 0, -4], [8, 37, 8]);
  r.box(lf, [64, 0], [-4, 0, -4], [8, 37, 8], 0, true);
  let roar = 0;
  return r.anim((v, q, headY, headX) => {
    // RavagerModel.setupAnim
    head.rotation.set(headX, headY, 0);
    const s = q.limbSwing * 0.6662, a = 0.4 * q.limbAmt;
    rh.rotation.x = Math.cos(s) * a; lh.rotation.x = Math.cos(s + PI) * a;
    rf.rotation.x = Math.cos(s + PI) * a; lf.rotation.x = Math.cos(s) * a;
    roar += ((v.color === 1 ? 1 : 0) - roar) * 0.25;
    // bite: the neck lunges and the jaw drops; roar: the jaw hangs wide open
    const bite = v.attackAnim > 0 ? Math.sin(v.attackAnim * PI) : 0;
    neck.position.z = 5.5 - bite * 6;
    mouth.rotation.x = Math.max(bite * 0.6, roar) * (PI / 2) * 0.9;
    head.rotation.x += roar * -0.3;
  });
}
